/**
 * One entry per UTC offset a venue can sit at, ordered west to east.
 *
 * `value` is the zone actually stored. Whole-hour offsets use the fixed-offset `Etc/GMT±N` zones,
 * whose sign the IANA database inverts by the POSIX convention — `Etc/GMT-2` is UTC+2. The thirteen
 * fractional offsets have no `Etc` equivalent and carry a representative real zone instead;
 * `label` is a fixed string either way and is never re-derived from the zone, so a backing zone
 * that observes DST cannot make its row relabel itself mid-year.
 *
 * DST-only offsets are left out: GMT-2:30 and GMT+13:45 exist solely as the summer time of
 * Newfoundland and Chatham, and no venue describes itself by them.
 *
 * `hint` is the few places shown beside the offset; `cities` is the full list behind it, searched
 * but never rendered, so a host can type any city and land on the right row.
 *
 * `event-timezones.test.ts` fails if any value drifts out of the API's accepted list.
 */
export const EVENT_TIMEZONE_OFFSETS = [
  {
    minutes: -660,
    value: 'Etc/GMT+11',
    label: 'GMT-11',
    hint: 'Midway, Niue, Pago Pago',
    cities: 'Midway, Niue, Pago Pago',
  },
  {
    minutes: -600,
    value: 'Etc/GMT+10',
    label: 'GMT-10',
    hint: 'Honolulu, Adak, Rarotonga, Tahiti',
    cities: 'Honolulu, Adak, Rarotonga, Tahiti',
  },
  {
    minutes: -570,
    value: 'Pacific/Marquesas',
    label: 'GMT-9:30',
    hint: 'Marquesas',
    cities: 'Marquesas',
  },
  {
    minutes: -540,
    value: 'Etc/GMT+9',
    label: 'GMT-9',
    hint: 'Anchorage, Gambier, Juneau, Metlakatla',
    cities: 'Anchorage, Gambier, Juneau, Metlakatla, Nome, Sitka, Yakutat',
  },
  {
    minutes: -480,
    value: 'Etc/GMT+8',
    label: 'GMT-8',
    hint: 'Los Angeles, Vancouver, Pitcairn, Tijuana',
    cities: 'Los Angeles, Vancouver, Pitcairn, Tijuana',
  },
  {
    minutes: -420,
    value: 'Etc/GMT+7',
    label: 'GMT-7',
    hint: 'Denver, Boise, Cambridge Bay, Creston',
    cities:
      'Denver, Boise, Cambridge Bay, Creston, Dawson, Dawson Creek, Edmonton, Fort Nelson, Hermosillo, Inuvik, Mazatlan, Phoenix, Whitehorse',
  },
  {
    minutes: -360,
    value: 'Etc/GMT+6',
    label: 'GMT-6',
    hint: 'Chicago, Mexico City, Bahia Banderas, Belize',
    cities:
      'Chicago, Mexico City, Bahia Banderas, Belize, Beulah, Center, Chihuahua, Costa Rica, Easter, El Salvador, Galapagos, Guatemala, Knox, Managua, Matamoros, Menominee, Merida, Monterrey, New Salem, Ojinaga, Rankin Inlet, Regina, Resolute, Swift Current, Tegucigalpa, Tell City, Winnipeg',
  },
  {
    minutes: -300,
    value: 'Etc/GMT+5',
    label: 'GMT-5',
    hint: 'New York, Toronto, Bogota, Lima',
    cities:
      'New York, Toronto, Bogota, Lima, Cancun, Cayman, Coral Harbour, Detroit, Eirunepe, Grand Turk, Guayaquil, Havana, Indianapolis, Iqaluit, Jamaica, Louisville, Marengo, Monticello, Nassau, Panama, Petersburg, Port-au-Prince, Rio Branco, Vevay, Vincennes, Winamac',
  },
  {
    minutes: -240,
    value: 'Etc/GMT+4',
    label: 'GMT-4',
    hint: 'Santiago, Anguilla, Antigua, Aruba',
    cities:
      'Santiago, Anguilla, Antigua, Aruba, Barbados, Bermuda, Blanc-Sablon, Boa Vista, Campo Grande, Caracas, Cuiaba, Curacao, Dominica, Glace Bay, Goose Bay, Grenada, Guadeloupe, Guyana, Halifax, Kralendijk, La Paz, Lower Princes, Manaus, Marigot, Martinique, Moncton, Montserrat, Port of Spain, Porto Velho, Puerto Rico, Santo Domingo, St Barthelemy, St Kitts, St Lucia, St Thomas, St Vincent, Thule, Tortola',
  },
  {
    minutes: -210,
    value: 'America/St_Johns',
    label: 'GMT-3:30',
    hint: 'St Johns',
    cities: 'St Johns',
  },
  {
    minutes: -180,
    value: 'Etc/GMT+3',
    label: 'GMT-3',
    hint: 'Sao Paulo, Buenos Aires, Araguaina, Asuncion',
    cities:
      'Sao Paulo, Buenos Aires, Araguaina, Asuncion, Bahia, Belem, Catamarca, Cayenne, Cordoba, Fortaleza, Jujuy, La Rioja, Maceio, Mendoza, Miquelon, Montevideo, Palmer, Paramaribo, Punta Arenas, Recife, Rio Gallegos, Rothera, Salta, San Juan, San Luis, Santarem, Stanley, Tucuman, Ushuaia',
  },
  {
    minutes: -120,
    value: 'Etc/GMT+2',
    label: 'GMT-2',
    hint: 'Noronha, Nuuk, Scoresbysund, South Georgia',
    cities: 'Noronha, Nuuk, Scoresbysund, South Georgia',
  },
  {
    minutes: -60,
    value: 'Etc/GMT+1',
    label: 'GMT-1',
    hint: 'Azores, Cape Verde',
    cities: 'Azores, Cape Verde',
  },
  {
    minutes: 0,
    value: 'UTC',
    label: 'GMT+0 · UTC',
    hint: 'London, Lisbon, Dublin, Abidjan',
    cities:
      'London, Lisbon, Dublin, Abidjan, Accra, Bamako, Banjul, Bissau, Canary, Conakry, Dakar, Danmarkshavn, Faroe Islands, Freetown, Guernsey, Isle of Man, Jersey, Lome, Madeira, Monrovia, Nouakchott, Ouagadougou, Reykjavik, Sao Tome, St Helena, Troll, UTC',
  },
  {
    minutes: 60,
    value: 'Etc/GMT-1',
    label: 'GMT+1',
    hint: 'Berlin, Paris, Madrid, Rome',
    cities:
      'Berlin, Paris, Madrid, Rome, Amsterdam, Lagos, Prague, Vienna, Warsaw, Stockholm, Brussels, Zurich, Algiers, Andorra, Bangui, Belgrade, Bratislava, Brazzaville, Budapest, Busingen, Casablanca, Ceuta, Copenhagen, Douala, El Aaiun, Gibraltar, Kinshasa, Libreville, Ljubljana, Longyearbyen, Luanda, Luxembourg, Malabo, Malta, Monaco, Ndjamena, Niamey, Oslo, Podgorica, Porto-Novo, San Marino, Sarajevo, Skopje, Tirane, Tunis, Vaduz, Vatican, Zagreb',
  },
  {
    minutes: 120,
    value: 'Etc/GMT-2',
    label: 'GMT+2',
    hint: 'Kyiv, Athens, Cairo, Johannesburg',
    cities:
      'Kyiv, Athens, Cairo, Johannesburg, Helsinki, Beirut, Blantyre, Bucharest, Bujumbura, Chisinau, Famagusta, Gaborone, Gaza, Harare, Hebron, Jerusalem, Juba, Kaliningrad, Khartoum, Kigali, Lubumbashi, Lusaka, Maputo, Mariehamn, Maseru, Mbabane, Nicosia, Riga, Sofia, Tallinn, Tripoli, Vilnius, Windhoek',
  },
  {
    minutes: 180,
    value: 'Etc/GMT-3',
    label: 'GMT+3',
    hint: 'Nairobi, Istanbul, Moscow, Addis Ababa',
    cities:
      'Nairobi, Istanbul, Moscow, Addis Ababa, Aden, Amman, Antananarivo, Asmara, Baghdad, Bahrain, Comoro, Damascus, Dar es Salaam, Djibouti, Kampala, Kirov, Kuwait, Mayotte, Minsk, Mogadishu, Qatar, Riyadh, Simferopol, Syowa, Volgograd',
  },
  {
    minutes: 210,
    value: 'Asia/Tehran',
    label: 'GMT+3:30',
    hint: 'Tehran',
    cities: 'Tehran',
  },
  {
    minutes: 240,
    value: 'Etc/GMT-4',
    label: 'GMT+4',
    hint: 'Dubai, Astrakhan, Baku, Mahe',
    cities:
      'Dubai, Astrakhan, Baku, Mahe, Mauritius, Muscat, Reunion, Samara, Saratov, Tbilisi, Ulyanovsk, Yerevan',
  },
  {
    minutes: 270,
    value: 'Asia/Kabul',
    label: 'GMT+4:30',
    hint: 'Kabul',
    cities: 'Kabul',
  },
  {
    minutes: 300,
    value: 'Etc/GMT-5',
    label: 'GMT+5',
    hint: 'Karachi, Almaty, Aqtau, Aqtobe',
    cities:
      'Karachi, Almaty, Aqtau, Aqtobe, Ashgabat, Atyrau, Dushanbe, Kerguelen, Maldives, Mawson, Oral, Qostanay, Qyzylorda, Samarkand, Tashkent, Vostok, Yekaterinburg',
  },
  {
    minutes: 330,
    value: 'Asia/Calcutta',
    label: 'GMT+5:30',
    hint: 'Kolkata, Colombo',
    cities: 'Kolkata, Colombo',
  },
  {
    minutes: 345,
    value: 'Asia/Katmandu',
    label: 'GMT+5:45',
    hint: 'Kathmandu',
    cities: 'Kathmandu',
  },
  {
    minutes: 360,
    value: 'Etc/GMT-6',
    label: 'GMT+6',
    hint: 'Dhaka, Bishkek, Chagos, Omsk',
    cities: 'Dhaka, Bishkek, Chagos, Omsk, Thimphu, Urumqi',
  },
  {
    minutes: 390,
    value: 'Asia/Rangoon',
    label: 'GMT+6:30',
    hint: 'Yangon, Cocos',
    cities: 'Yangon, Cocos',
  },
  {
    minutes: 420,
    value: 'Etc/GMT-7',
    label: 'GMT+7',
    hint: 'Bangkok, Jakarta, Barnaul, Christmas',
    cities:
      'Bangkok, Jakarta, Barnaul, Christmas, Davis, Ho Chi Minh City, Hovd, Krasnoyarsk, Novokuznetsk, Novosibirsk, Phnom Penh, Pontianak, Tomsk, Vientiane',
  },
  {
    minutes: 480,
    value: 'Etc/GMT-8',
    label: 'GMT+8',
    hint: 'Singapore, Hong Kong, Shanghai, Manila',
    cities:
      'Singapore, Hong Kong, Shanghai, Manila, Perth, Brunei, Casey, Irkutsk, Kuala Lumpur, Kuching, Macau, Makassar, Taipei, Ulaanbaatar',
  },
  {
    minutes: 525,
    value: 'Australia/Eucla',
    label: 'GMT+8:45',
    hint: 'Eucla',
    cities: 'Eucla',
  },
  {
    minutes: 540,
    value: 'Etc/GMT-9',
    label: 'GMT+9',
    hint: 'Tokyo, Seoul, Chita, Dili',
    cities: 'Tokyo, Seoul, Chita, Dili, Jayapura, Khandyga, Palau, Pyongyang, Yakutsk',
  },
  {
    minutes: 570,
    value: 'Australia/Darwin',
    label: 'GMT+9:30',
    hint: 'Adelaide, Darwin, Broken Hill',
    cities: 'Adelaide, Darwin, Broken Hill',
  },
  {
    minutes: 600,
    value: 'Etc/GMT-10',
    label: 'GMT+10',
    hint: 'Sydney, Melbourne, Brisbane, Chuuk',
    cities:
      'Sydney, Melbourne, Brisbane, Chuuk, DumontDUrville, Guam, Hobart, Lindeman, Macquarie, Port Moresby, Saipan, Ust-Nera, Vladivostok',
  },
  {
    minutes: 630,
    value: 'Australia/Lord_Howe',
    label: 'GMT+10:30',
    hint: 'Lord Howe',
    cities: 'Lord Howe',
  },
  {
    minutes: 660,
    value: 'Etc/GMT-11',
    label: 'GMT+11',
    hint: 'Bougainville, Efate, Guadalcanal, Kosrae',
    cities:
      'Bougainville, Efate, Guadalcanal, Kosrae, Magadan, Norfolk, Noumea, Pohnpei, Sakhalin, Srednekolymsk',
  },
  {
    minutes: 720,
    value: 'Etc/GMT-12',
    label: 'GMT+12',
    hint: 'Auckland, Anadyr, Fiji, Funafuti',
    cities:
      'Auckland, Anadyr, Fiji, Funafuti, Kamchatka, Kwajalein, Majuro, McMurdo, Nauru, Tarawa, Wake, Wallis',
  },
  {
    minutes: 765,
    value: 'Pacific/Chatham',
    label: 'GMT+12:45',
    hint: 'Chatham',
    cities: 'Chatham',
  },
  {
    minutes: 780,
    value: 'Etc/GMT-13',
    label: 'GMT+13',
    hint: 'Apia, Enderbury, Fakaofo, Tongatapu',
    cities: 'Apia, Enderbury, Fakaofo, Tongatapu',
  },
  {
    minutes: 840,
    value: 'Etc/GMT-14',
    label: 'GMT+14',
    hint: 'Kiritimati',
    cities: 'Kiritimati',
  },
] as const;
